import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import * as React from 'react';
import {faPlayCircle} from '@fortawesome/pro-regular-svg-icons';
import {faTimes} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Navigation, Pagination, Thumbs, Zoom, Keyboard} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import {useEffect, useState} from 'react';

import {Alerts} from '../../ui/Alerts/Alerts';
import {ItemsService} from '../../../client/items/items.service';
import {Item} from '../item.class';
import {ModalBackdrop} from '../../ui/modals/ModalBackdrop';
import {Result} from '../../search/search.types';
import {swiperSlideStyles} from '../../ui/Swiper/SwiperClasses';
import {useService} from '../../react/ServiceContext';

interface ImageZoomModalProps {
    activeIndex: number;
    item?: Item;
    resultItem?: Result;
    onHide: () => void;
    show: boolean;
}

interface ImageSlide {
    name: string;
    _id: string;
    referenceType: string;
    imagePath: string;
    description?: string;
}

export const ImageZoomModal = ({activeIndex, item, resultItem, onHide, show}: ImageZoomModalProps) => {
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [errorMessage, setErrorMessage] = useState(``);
    const [slides, setSlides] = useState<ImageSlide[]>([]);
    const itemsService: ItemsService = useService(`itemsService`);

    useEffect(() => {
        if (!show) {
            setErrorMessage('');
            setThumbsSwiper(null);
        }
    }, [show]);

    useEffect(() => {
        if (!resultItem) {
            return;
        }
        if (!show) {
            return;
        }
        itemsService
            .getSecondaryImages(resultItem.id)
            .then((itemWithImages) => {
                const newSlides: ImageSlide[] = [];

                const primary = {
                    name: resultItem.name || 'Primary Image',
                    imagePath: resultItem.imagePath_s,
                    referenceType: 'primary',
                    _id: resultItem.id,
                };
                newSlides.push(primary);
                if (itemWithImages.secondaryImages && itemWithImages.secondaryImages.length > 0) {
                    itemWithImages.secondaryImages.forEach((secondaryAsset) => {
                        newSlides.push({
                            name: secondaryAsset.name,
                            imagePath: secondaryAsset.fileLocation.replace('ProductImageAssets', ''),
                            referenceType: secondaryAsset.referenceType,
                            description: secondaryAsset.description,
                            _id: secondaryAsset._id,
                        });
                    });
                }
                setSlides(newSlides);
            })
            .catch(() => {
                setErrorMessage('Error Loading images');
            });
    }, [itemsService, resultItem, show]);

    useEffect(() => {
        if (!item) {
            return;
        }
        const newSlides: ImageSlide[] = [];
        newSlides.push({
            name: item.name || 'Primary Image',
            imagePath: item.imagePath,
            referenceType: 'primary',
            description: item.primaryImageDesc,
            _id: item.id,
        });
        if (item.secondaryImages && item.secondaryImages.length > 0) {
            item.secondaryImages.forEach((secondaryAsset) => {
                newSlides.push({
                    name: secondaryAsset.name,
                    imagePath: secondaryAsset.fileLocation.replace('ProductImageAssets', ''),
                    referenceType: secondaryAsset.referenceType,
                    description: secondaryAsset.description,
                    _id: secondaryAsset._id,
                });
            });
        }
        setSlides(newSlides);
    }, [item, show]);

    /**
     * Template
     */
    return (
        <>
            <ModalBackdrop
                onClick={onHide}
                show={show}
            />
            <div
                data-e2e="ImageZoomModal"
                className={`tw-bg-white tw-opacity-100 tw-fixed tw-top-0 tw-left-0 tw-z-[1050] tw-w-full tw-h-full tw-outline-0 tw-p-0 !tw-max-w-full md:!tw-max-w-[calc(100%-4.0rem)]${
                    show ? '' : ' tw-hidden'
                }`}
            >
                <div className="tw-w-full tw-h-full tw-max-h-full tw-m-0 tw-top-0 !tw-max-w-full sm:tw-min-h-[calc(100%-3.5rem)] tw-flex tw-items-center tw-relative tw-pointer-events-none before:tw-block before:tw-content-[''] before:tw-h-min">
                    <div className="tw-h-full tw-relative tw-flex tw-flex-col tw-w-full tw-pointer-events-auto tw-bg-white tw-outline-0">
                        <Alerts
                            message={errorMessage}
                            variant="warning"
                        />
                        <div className="tw-text-right tw-pr-3 tw-pt-3">
                            <FontAwesomeIcon
                                icon={faTimes}
                                size="3x"
                                onClick={() => onHide()}
                            />
                        </div>
                        <div className="tw-h-full tw-overflow-y-auto tw-relative tw-p-8">
                            <div className="tw-h-full">
                                <div
                                    className={`tw-h-full [&_.swiper-button-prev]:tw-mt-20 [&_.swiper-button-next]:tw-mt-20 ${swiperSlideStyles}`}
                                >
                                    <div className="tw-flex tw-flex-col tw-h-full">
                                        <div className="tw-text-gray-400 tw-text-center">Double tap image to magnify</div>
                                        <Swiper
                                            className="tw-grow tw-w-full"
                                            navigation
                                            slidesPerView={1}
                                            centeredSlides
                                            initialSlide={activeIndex}
                                            thumbs={{swiper: thumbsSwiper}}
                                            modules={[Navigation, Pagination, Thumbs, Zoom, Keyboard]}
                                            zoom={true}
                                        >
                                            {slides.map((slide, index) => (
                                                <SwiperSlide key={index}>
                                                    <div className="tw-flex tw-items-center tw-justify-center tw-h-full">
                                                        {slide.referenceType === 'IMPRL_Video' ? (
                                                            <div className="tw-relative tw-block tw-w-full tw-p-0 tw-overflow-hidden tw-h-full before:tw-block before:tw-content-[''] before:tw-pt-[56.25%] tw-m-auto tw-max-h-full">
                                                                <iframe
                                                                    src={`https://www.youtube.com/embed/${slide._id}`}
                                                                    className="tw-absolute tw-top-0 tw-bottom-0 tw-left-0 tw-w-full tw-h-full tw-border-0"
                                                                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                                                    allowFullScreen={true}
                                                                ></iframe>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="swiper-zoom-container">
                                                                    <img
                                                                        alt={slide.name}
                                                                        style={{maxHeight: 'calc(100vh - 250px)', maxWidth: '100%'}}
                                                                        src={`/ProductImageThumbs1000${slide.imagePath || '/noimage.png'}`}
                                                                    />
                                                                </div>
                                                                <Alerts
                                                                    message={slide.description}
                                                                    variant="light"
                                                                />
                                                            </>
                                                        )}
                                                    </div>
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                        <div className="tw-w-full tw-flex tw-justify-center tw-mt-2 [&_.swiper-slide-thumb-active]:tw-border-[3px] [&_.swiper-slide-thumb-active]:tw-border-gray-700">
                                            <Swiper
                                                watchSlidesProgress
                                                onSwiper={setThumbsSwiper}
                                                spaceBetween={8}
                                                slidesPerView={'auto'}
                                                freeMode
                                                style={{height: 100}}
                                            >
                                                {slides.map((slide, index) => (
                                                    <SwiperSlide
                                                        key={index}
                                                        className="tw-flex tw-border tw-p-2 tw-justify-center tw-content-center"
                                                        style={{maxWidth: '67px', maxHeight: '67px', boxSizing: 'content-box'}}
                                                    >
                                                        <img
                                                            alt={slide.name}
                                                            height={67}
                                                            width={67}
                                                            className="tw-object-contain"
                                                            src={`/ProductImageThumbs67${slide.imagePath || '/noimage.png'}`}
                                                        />
                                                        {slide.referenceType === 'IMPRL_Video' && (
                                                            <FontAwesomeIcon
                                                                icon={faPlayCircle}
                                                                className="tw-absolute tw-z-[200] tw-top-1/2 tw-left-1/2 -tw-translate-y-2/4 tw-text-center tw-shadow-[0_2px_0_rgba(0,0,0,0.5)] -tw-translate-x-2/4"
                                                                color="white"
                                                                size="2x"
                                                            />
                                                        )}
                                                    </SwiperSlide>
                                                ))}
                                            </Swiper>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
